import React from "react";
import HeaderImage from "../HeaderImage";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CircleDot } from "lucide-react";

const workshopActivities = [
  {
    title: `<h4> <a
                      href="https://indico.wacren.net/event/176/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                    >Research Data Management (RDM) Workshop</a></h4>`,
    subTitle: `<span>– Principles, Policies, and Practices</span>`,
    date: "November 2023",
    link: "https://indico.wacren.net/event/176/",
    description: `<p className="mb-4">
      Held during the 19th RUFORUM AGM in Yaoundé, Cameroon, this workshop focused on strengthening RDM and promoting open agricultural data sharing in Africa.
      </p>`,
    image: "/assets/images/workshop/LIBSENSE_RUFORUM.jpg",
    reverse: false,
  },
  {
    title: `<h4> <p
                      href="https://indico.wacren.net/event/176/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                    >Continental SURVEY</p></h4>`,
    subTitle: `<span>– Needs Assessment Questionnaire for the Development of an Open Data Repository</span>`,
    date: "September 2023",
    description:
      " Aimed to gather input on the requirements for an open data repository within the RUFORUM community.",
    image: "/assets/images/workshop/survey.webp",
    reverse: true,
  },
  {
    title: `<h4> <a
    href="https://indico.wacren.net/event/230/"
    target="_blank"
    rel="noopener noreferrer"
    className="text-primary"
  >LIBSENSE-RUFORUM Webinar on Open Science and RDM</a></h4>`,
    date: "February 2023",
    subTitle: ``,
    link: "https://indico.wacren.net/event/230/",
    description: ` <p className="mb-4">
                    This
                    <a
                      href="https://survey.wacren.net/index.php/516664"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                    >
                      webinar
                    </a>
                    educated participants on open science and RDM concepts, setting the stage for the needs assessment survey.
                  </p>`,
    image: "/assets/images/workshop/Workshop-Webinar-2.jpg",
    reverse: false,
  },
  {
    title: `<h4> <a
    href="https://indico.wacren.net/event/231/"
    target="_blank"
    rel="noopener noreferrer"
    className="text-primary"
  >Workshop on Needs Assessment Validation for Open Data Repository</a></h4>`,
    subTitle: ``,
    date: "December 2022",
    link: "https://indico.wacren.net/event/231/",
    description: `   <p className="mb-4">
                   Held in Harare, Zimbabwe, the  <a
                      href="https://ruforum.org/AGM2023/sites/default/files/Concept%20note%20RUFORUM-LIBSENSE%20RDM.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                    >
                      workshop
                    </a> validated the needs assessment tool and emphasised raising awareness about key concepts in the survey.
                  </p>`,
    image: "/assets/images/workshop/WORKSHOP-UPDATE.jpg",
    reverse: true,
  },
];

const activities = [
  {
    title: `<p><strong>Developed Technical Specifications</strong> for the open data repository</p>`,
    date: "November 2024",
  },
  {
    title: `<p><strong>Policy Survey:</strong> Shared with the RUFORUM community to address policy issues</p>`,
    date: "September 2024",
  },
  {
    title: `<p><strong>First Stakeholder Advisory Group (SAG) Meeting</strong></p>`,
    date: "September 2024",
  },
  {
    title: `<p><strong>Policy Guidelines:</strong> Drafts of data sharing and data protection policies developed</p>`,
    date: "August 2024",
  },
  {
    title: `<p><strong>Establishment of the Stakeholder Advisory Group (SAG) </strong></p>`,
    date: "August 2024",
  },
];
const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <motion.h3
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="text-2xl font-semibold text-foreground mb-6"
  >
    {children}
  </motion.h3>
);

const RuforumPage: React.FC = () => {
  return (
    <>
      <HeaderImage image="/assets/images/backgrounds/collab.jpg" />

      <section className="container mx-auto px-4 md:px-8">
        <motion.h2
          className="mt-10 text-xxl font-bold text-foreground mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          LIBSENSE-RUFORUM Partnership
        </motion.h2>

        <motion.div
          className="space-y-4 text-sm text-foreground leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <p className="mt-4">
            In 2021, the{" "}
            <Link
              target="_blank"
              to="https://wacren.net/en/"
              className="text-primary"
            >
              West and Central African Research and Education Network (WACREN)
            </Link>{" "}
            on behalf of LIBSENSE, signed a three-year Memorandum of
            Understanding (MoU) with the Regional Universities Forum for
            Capacity Building in Agriculture ({" "}
            <Link
              target="_blank"
              to="https://www.ruforum.org/"
              className="text-primary"
            >
              RUFORUM
            </Link>
            ) to advance scientific and scholarly communication across Africa.
            This collaboration, part of the{" "}
            <Link
              target="_blank"
              to="https://africaconnect3.net/"
              className="text-primary"
            >
              AfricaConnect3
            </Link>{" "}
            project, focuses on creating modern platforms for agricultural
            research data sharing and open access publishing.
          </p>

          <p>
            Following a successful initial phase, the{" "}
            <Link
              target="_blank"
              to="https://wacren.net/en/news/ruforum-and-wacren-renew-cooperation-to-expand-open-science-and-digital-services-in-africa/"
              className="text-primary"
            >
              MoU was renewed in 2023
            </Link>{" "}
            for another two years. The primary goals of this ongoing partnership
            include building capacity in research data management (RDM),
            developing open access repositories (OARs), promoting open science
            policies, and boosting the impact of research within Africa's
            agricultural sector.
          </p>
          <p>
            Through this collaboration, LIBSENSE and RUFORUM have undertaken key
            initiatives, including conducting a needs assessment survey to
            identify the requirements for an open data repository and inform its
            design and development.
          </p>
        </motion.div>

        {/* Activities Section */}
        <section className="my-16">
          <SectionHeading>Key Activities and Milestones</SectionHeading>
          <motion.ul
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            transition={{ duration: 0.8 }}
            className="relative border-l border-primary pl-6 space-y-8"
          >
            {activities.map((activity, index) => (
              <li key={index} className="relative">
                <div className="absolute -left-5 top-1 text-primary">
                  <CircleDot size={20} />
                </div>
                <div
                  className="activity-content"
                  dangerouslySetInnerHTML={{ __html: activity.title }}
                ></div>
                <time className="block text-sm text-muted">
                  {activity.date}
                </time>
              </li>
            ))}
          </motion.ul>
        </section>

        {/* Workshop Section */}
        <section className="mt-20 mb-10">
          <SectionHeading>Workshops & Webinars</SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {workshopActivities.map((event, index) => (
              <motion.div
                key={index}
                className="bg-card rounded-2xl shadow-lg p-6 flex flex-col gap-2 border border-border hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {/* Title */}
                <div
                  className="text-lg font-semibold text-primary"
                  dangerouslySetInnerHTML={{ __html: event.title }}
                />

                {/* Subtitle */}
                {event.subTitle && (
                  <div
                    className="text-sm text-muted-foreground mb-1"
                    dangerouslySetInnerHTML={{ __html: event.subTitle }}
                  />
                )}

                {/* Date badge */}
                <div className="inline-block bg-muted px-3 py-1 text-xs font-medium rounded-full w-max mb-2 text-muted-foreground">
                  {event.date}
                </div>

                {/* Description */}
                <div
                  className="text-sm leading-relaxed text-foreground"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </motion.div>
            ))}
          </div>
        </section>

        {/* Activities Section */}
        {/* <section className="my-16">
          <SectionHeading>Key Activities</SectionHeading>
          <motion.ul
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
            transition={{ duration: 0.6 }}
            className="relative border-l border-primary pl-6 space-y-8"
          >
            {activities.map((activity, index) => (
              <li key={index} className="relative">
                <div className="absolute -left-5 top-1 text-primary">
                  <CircleDot size={20} />
                </div>
                <p className="text-base font-medium">{activity.title}</p>
                <time className="block text-sm text-muted mt-1">
                  {activity.date}
                </time>
              </li>
            ))}
          </motion.ul>
        </section> */}
      </section>
    </>
  );
};

export default RuforumPage;
